import React from 'react';
import {
    Box,
    TextField,
    IconButton,
    Radio,
    Tooltip,
    Button,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useTranslation } from 'react-i18next';

/**
 * Editor for multiple prices on a client item.
 * value: [{ id?, name, amount, isDefault, _key? }]
 */
export default function ClientItemPricesEditor({
    value = [],
    onChange,
    direction = 'rtl',
    inputPropsStyle,
    inputStyle,
}) {
    const { t } = useTranslation();
    const prices = Array.isArray(value) && value.length > 0
        ? value
        : [{ id: null, name: '', amount: '', isDefault: true, _key: 'default-0' }];

    const emit = (next) => {
        if (typeof onChange === 'function') onChange(next);
    };

    const handleField = (index, field, fieldValue) => {
        const next = prices.map((p, i) =>
            i === index ? { ...p, [field]: fieldValue } : p
        );
        emit(next);
    };

    const handleSetDefault = (index) => {
        emit(prices.map((p, i) => ({ ...p, isDefault: i === index })));
    };

    const handleAdd = () => {
        emit([
            ...prices,
            {
                id: null,
                name: '',
                amount: '',
                isDefault: prices.length === 0,
                _key: `new-${Date.now()}`,
            },
        ]);
    };

    const handleRemove = (index) => {
        if (prices.length <= 1) return;
        const next = prices.filter((_, i) => i !== index);
        if (!next.some((p) => p.isDefault) && next.length > 0) {
            next[0] = { ...next[0], isDefault: true };
        }
        emit(next);
    };

    const fieldInputProps = inputPropsStyle || {
        disableUnderline: true,
        sx: {
            height: '40px',
            padding: '0 8px',
            borderRadius: '8px',
            background: '#F5F5F6',
        },
    };

    return (
        <Box sx={{ width: '100%' }}>
            <div className="edit-form-row-item-title" style={{ marginBottom: 8 }}>
                {t('ItemPrices')}
            </div>
            {prices.map((row, index) => (
                <Box
                    key={row._key || row.id || index}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 1,
                        flexWrap: 'wrap',
                    }}
                >
                    <Tooltip title={t('DefaultPrice')}>
                        <Radio
                            checked={!!row.isDefault}
                            onChange={() => handleSetDefault(index)}
                            size="small"
                            sx={{ color: '#304FFF', '&.Mui-checked': { color: '#304FFF' } }}
                        />
                    </Tooltip>
                    <TextField
                        placeholder={t('PriceName')}
                        value={row.name || ''}
                        onChange={(e) => handleField(index, 'name', e.target.value)}
                        variant="standard"
                        InputProps={fieldInputProps}
                        inputProps={inputStyle}
                        sx={{ flex: '1 1 120px', minWidth: 100 }}
                    />
                    <TextField
                        type="number"
                        placeholder={t('Price')}
                        value={row.amount ?? ''}
                        onChange={(e) => handleField(index, 'amount', e.target.value)}
                        variant="standard"
                        required
                        InputProps={fieldInputProps}
                        inputProps={inputStyle}
                        error={row.amount === '' || row.amount == null}
                        sx={{ flex: '1 1 100px', minWidth: 90 }}
                    />
                    {prices.length > 1 && (
                        <Tooltip title={t('RemovePrice')}>
                            <IconButton size="small" onClick={() => handleRemove(index)}>
                                <DeleteOutlineIcon sx={{ color: 'red' }} />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            ))}
            <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={handleAdd}
                sx={{ color: '#304FFF', mt: 0.5 }}
            >
                {t('AddPrice')}
            </Button>
        </Box>
    );
}

export function arePricesValid(prices) {
    if (!Array.isArray(prices) || prices.length === 0) return false;
    return prices.every((p) => p.amount !== '' && p.amount != null && !Number.isNaN(Number(p.amount)));
}
